<template>
  <div
    class="credit-summary"
    data-test="credit-summary"
  >
    <div class="credit-summary__header">
      <div
        class="credit-summary__header-title"
        data-test="credit-summary-title"
      >
        Total usage
      </div>
      <div
        class="credit-summary__header-value"
        data-test="credit-summary-value"
      >
        {{ formattedUsed }}&nbsp;/&nbsp;{{ formattedAvaiable }} used
      </div>
    </div>
    <div class="credit-summary__bar">
      <CreditBar
        :bars="bars"
        :total="available || 0"
      />
    </div>
    <div class="credit-summary__content">
      <ExpansionPanels class="credit-summary__content-expansion">
        <template
          v-for="(usage, idx) in values"
          :key="usage?.id ?? idx"
        >
          <ExpansionPanel
            v-if="usage"
            :id="usage.id"
            class="credit-summary__content-expansion-item"
            :data-test="`credit-usage-panel-${usage.isChild ? `child-${idx}` : 'parent'}`"
          >
            <template #header>
              <div class="credit-summary__content-expansion-header">
                <div class="credit-summary__content-expansion-header-left">
                  <div
                    class="credit-summary__content-expansion-header-left-icon"
                    :style="{ backgroundColor: usage.color }"
                  />
                  <div
                    class="credit-summary__content-expansion-header-left-title"
                    :data-test="`credit-usage-name-${usage.isChild ? `child-${idx}` : 'parent'}`"
                  >
                    {{ usage.name }}
                  </div>
                </div>
                <div class="credit-summary__content-expansion-header-right">
                  <div
                    v-if="usage.credit.limit"
                    class="credit-summary__content-expansion-header-right-limit"
                    :data-test="`credit-usage-limit-${usage.isChild ? `child-${idx}` : 'parent'}`"
                  >
                    <ToolTip content="Update limits in the 'Team credit limits' section below.">
                      {{ formatLongNumericString(`${usage.credit.limit}`, ',') }} max.
                    </ToolTip>
                  </div>
                  <div
                    class="credit-summary__content-expansion-header-right-used"
                    :class="{
                      'credit-summary__content-expansion-header-right-used--exceeded':
                        usage.credit.limit && usage.credit.used >= usage.credit.limit,
                    }"
                    :data-test="`credit-usage-total-${usage.isChild ? `child-${idx}` : 'parent'}`"
                  >
                    {{ formatLongNumericString(`${usage.credit.used}`, ',') }} used
                  </div>
                </div>
              </div>
            </template>
            <template #content>
              <div class="credit-summary__content-expansion-content">
                <div class="credit-summary__content-expansion-content-item">
                  <div class="credit-summary__content-expansion-content-item-label">
                    Human hours
                  </div>
                  <div
                    class="credit-summary__content-expansion-content-item-value"
                    :data-test="`credit-usage-human-${usage.isChild ? `child-${idx}` : 'parent'}`"
                  >
                    {{ formatLongNumericString(`${usage.credit.human}`, ',') }} used
                  </div>
                </div>
                <div class="credit-summary__content-expansion-content-item">
                  <div class="credit-summary__content-expansion-content-item-label">Automation</div>
                  <div
                    class="credit-summary__content-expansion-content-item-value"
                    :data-test="`credit-usage-automation-${usage.isChild ? `child-${idx}` : 'parent'}`"
                  >
                    {{ formatLongNumericString(`${usage.credit.automation}`, ',') }} used
                  </div>
                </div>
                <div class="credit-summary__content-expansion-content-item">
                  <div class="credit-summary__content-expansion-content-item-label">
                    Model uptime
                  </div>
                  <div
                    class="credit-summary__content-expansion-content-item-value"
                    :data-test="`credit-usage-model-${usage.isChild ? `child-${idx}` : 'parent'}`"
                  >
                    {{ formatLongNumericString(`${usage.credit.model}`, ',') }} used
                  </div>
                </div>
              </div>
            </template>
          </ExpansionPanel>
        </template>
      </ExpansionPanels>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import CreditBar from './CBBCreditBar.vue'
import { formatLongNumericString } from '@/core/utils/formatter'
import ExpansionPanel from '@/components/Common/ExpansionPanels/ExpansionPanel.vue'
import ExpansionPanels from '@/components/Common/ExpansionPanels/ExpansionPanels.vue'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import type { UsageInfo } from './types'

const props = defineProps<{
  available: number
  used: number
  values: Omit<UsageInfo, 'storage'>[]
}>()

const bars = computed(() =>
  props.values.map(({ color, credit }) => ({
    value: credit.used,
    color,
  })),
)

const formattedUsed = computed(() => formatLongNumericString(`${props.used}`, ','))

const formattedAvaiable = computed(() => formatLongNumericString(`${props.available}`, ','))
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/fonts.scss';

.credit-summary {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-self: center;
  gap: var(--size-4);
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-title {
      @include fontRegularBody100;
      color: var(--color-text-default);
      font-weight: 600;
    }

    &-value {
      @include fontRegularBody100;
      color: var(--color-text-default);
      font-weight: 500;
    }
  }

  &__content-expansion {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: calc(100% + var(--size-4));
    margin: 0 calc(var(--size-2) * -1);
    padding-bottom: 0;

    &-item {
      width: 100%;
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--size-2);

      &-left {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: var(--size-2);

        &-icon {
          width: var(--size-2);
          height: var(--size-2);
          border-radius: 50%;
        }

        &-title {
          @include fontRegularBody100;
          color: var(--color-text-default);
        }
      }

      &-right {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: var(--size-1);

        &-used {
          @include fontRegularBody100;
          color: var(--color-text-subtle);

          &--exceeded {
            color: var(--color-text-warning);
          }
        }
      }
    }

    &-content {
      display: flex;
      padding: 0 30px 0 var(--size-10);
      flex-direction: column;
      gap: var(--size-2);

      &-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: var(--size-5);
        gap: var(--size-1);

        &-label {
          @include fontRegularBody100;
          color: var(--color-text-subtle);
        }

        &-value {
          @include fontRegularBody100;
          color: var(--color-text-subtlest);
        }
      }
    }
  }
}
</style>
